import { createApolloClient } from 'core/services/apollo/createApolloClient';
import { SaveCampaignDocument } from 'generated-graphql/graphql';
import {
  Channels,
  ClosingMethod,
  UpdateCampaignInput
} from 'generated-graphql/types';
import { CampaignCreationStoreState } from 'modules/campaign-creation/stores/CampaignCreationStore';
import { mapQuestionsPayloadV3 } from 'modules/campaign-creation/utils/mapQuestionsPayloadV3';

// NOTE: When something is being passed to the store as undefined ignore it
//       When something is being passed to the store as null, save it as null
export const handleStoreSaveCampaign = async (
  campaignId: string | null,
  state: Partial<CampaignCreationStoreState>
) => {
  const client = createApolloClient();

  if (!state || !campaignId) return;

  if (!state.campaignType) {
    throw new Error('Missing campaign type');
  }

  const input: UpdateCampaignInput = {
    id: campaignId,
    isDuplicate: state.isDuplicate ?? null
  };

  input.type = state.campaignType;

  // ENGAGEMENT_DESCRIPTION STEP
  if (state.engagementDescriptionState) {
    input.outreachDescription =
      state.engagementDescriptionState.outreachDescription;
    input.title = state.engagementDescriptionState.title;
  } else if (state.engagementDescriptionState === null) {
    input.outreachDescription = null;
  }

  // JOB_DESCRIPTION STEP
  if (state.noATSJobDescriptionState) {
    input.jobDescriptionText =
      state.noATSJobDescriptionState.jobDescriptionText;
    input.externalId = state.noATSJobDescriptionState.externalId;
  } else if (state.noATSJobDescriptionState === null) {
    input.jobDescriptionText = null;
    input.externalId = null;
  }

  // ATS_JOB_DESCRIPTION STEP
  if (state.ATSJobDescriptionState) {
    input.jobDescriptionText =
      state.ATSJobDescriptionState.selectedJob.jobDescriptionText;
    input.atsJobId = state.ATSJobDescriptionState.selectedJob.atsJobId;
    input.atsJobPostingId =
      state.ATSJobDescriptionState.selectedJob.atsJobPostingId;
  } else if (state.ATSJobDescriptionState === null) {
    input.atsJobId = null;
    input.atsJobPostingId = null;
  }

  // EDIT_JOB_DESCRIPTION STEP
  if (state.jobDescriptionParsedState) {
    input.jobDescriptionParsed =
      state.jobDescriptionParsedState.jobDescriptionParsed;
    input.title = state.jobDescriptionParsedState.jobDescriptionParsed.jobTitle;
    input.additionalContext =
      state.jobDescriptionParsedState.additionalContext ?? null;
  } else if (state.jobDescriptionParsedState === null) {
    input.jobDescriptionParsed = null;
    input.additionalContext = null;
  }

  // ATS_IMPORT STEP
  if (state.ATSImportState) {
    input.atsListId = state.ATSImportState.atsListId;
  } else if (state.ATSImportState === null) {
    input.atsListId = null;
  }

  // CSV_IMPORT STEP
  if (state.CSVImportState) {
    input.csvS3Key = state.CSVImportState.csvS3Key;
  } else if (state.CSVImportState === null) {
    input.csvS3Key = null;
  }

  // QUESTIONS_AND_DOCUMENTS STEP
  if (state.questions) {
    input.questions = mapQuestionsPayloadV3(state.questions || []);
  } else if (state.questions === null) {
    input.questions = null;
  }

  // CHANNEL STEP
  if (state.channel) {
    input.channel = state.channel;
  } else if (state.channel === null) {
    input.channel = null;
  }

  // boolean, so a truthiness check would drop an explicit false
  if (state.emailChannelFallbackEnabled !== undefined) {
    input.emailChannelFallbackEnabled = state.emailChannelFallbackEnabled;
  }

  // SETUP_AND_SIMULATION STEP
  if (state.setupAndSimulationFields) {
    input.agentId = state.setupAndSimulationFields.agentId || null;
    input.closingMethod =
      (state.setupAndSimulationFields
        ?.closingMethod as unknown as ClosingMethod) || null;
    input.templateId =
      state.channel === Channels.Email
        ? null
        : state.setupAndSimulationFields.templateId;
    input.customTemplatedMessage =
      state.setupAndSimulationFields.customTemplatedMessage || null;
    input.customTemplatedSubjectLine =
      state.setupAndSimulationFields.customTemplatedSubjectLine || null;
    input.calendarMeetingTemplateId =
      state.setupAndSimulationFields.calendarMeetingTemplateId || null;
    input.customRequest = state.setupAndSimulationFields.customRequest || null;
    input.rejectionMessage =
      state.setupAndSimulationFields.rejectionMessage || null;
    input.closingMessage =
      state.setupAndSimulationFields.closingMessage || null;
    input.timeToAutoCloseConversationsInHours =
      state.setupAndSimulationFields.timeToAutoCloseConversationsInHours ||
      null;
  }

  await client.mutate({
    mutation: SaveCampaignDocument,
    variables: {
      input
    }
  });
};
